import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { Button } from '@/shared/components/ui/button';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/shared/components/ui/input-otp';
import { useVerifyRecoveryCode } from '../hooks/useVerifyRecoveryCode';
import { createVerifyRecoveryCodeSchema, type VerifyRecoveryCodeFormValues } from '../schemas';

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function VerifyRecoveryCodePage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email ?? '';

  const {
    verifyCode,
    isPending,
    isSuccess,
    error,
    resendCode,
    isResending,
    resendError,
    secondsLeft,
    isExpired,
  } = useVerifyRecoveryCode(email);

  const { control, handleSubmit } = useForm<VerifyRecoveryCodeFormValues>({
    resolver: zodResolver(createVerifyRecoveryCodeSchema(t)),
    defaultValues: { code: '' },
  });

  const onSubmit = ({ code }: VerifyRecoveryCodeFormValues) => {
    verifyCode(code);
  };

  useEffect(() => {
    if (isSuccess) {
      navigate('/reset-password', { replace: true, state: { verified: true } });
    }
  }, [isSuccess, navigate]);

  // Sin correo en el state de navegación: no vino del paso 1 — no hay a
  // qué correo verificar el código.
  if (!email) {
    return (
      <Card className="w-full border-0 shadow-none bg-transparent">
        <CardHeader className="text-center">
          <CardTitle className="text-base font-medium">
            {t('verifyRecoveryCode.missingEmailTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <p className="text-muted-foreground text-sm text-center mb-4">
            {t('verifyRecoveryCode.missingEmailDesc')}
          </p>
          <Button variant="outline" className="w-full" onClick={() => navigate('/forgot-password')}>
            {t('resetPassword.backToForgot')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <Card className="w-full border-0 shadow-none bg-transparent">
        <CardContent className="space-y-5 px-0">
          {error && (
            <Alert variant="destructive" data-testid="auth-verify-error-alert">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
          {resendError && (
            <Alert variant="destructive" data-testid="auth-verify-resend-error-alert">
              <AlertDescription>{resendError.message}</AlertDescription>
            </Alert>
          )}

          <p className="text-foreground text-base font-medium text-center w-full">
            {t('verifyRecoveryCode.subtitle', { email })}
          </p>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-5"
            data-testid="auth-verify-code-form"
          >
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={resendCode}
                disabled={isResending}
                className="text-sm text-primary text-left hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="auth-verify-resend-code"
              >
                {isResending ? t('verifyRecoveryCode.resending') : t('verifyRecoveryCode.resend')}
              </button>

              <Controller
                control={control}
                name="code"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <InputOTP
                      maxLength={6}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isExpired}
                      containerClassName="w-full"
                      data-testid="auth-verify-code-input"
                    >
                      <InputOTPGroup className="w-full justify-between gap-2">
                        {[0, 1, 2, 3, 4, 5].map((index) => (
                          <InputOTPSlot
                            key={index}
                            index={index}
                            className="size-12 rounded-lg border border-input first:rounded-lg first:border-l last:rounded-lg data-[active=true]:border-2 data-[active=true]:border-primary data-[active=true]:ring-0"
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                    {fieldState.invalid && (
                      <p className="text-destructive text-xs">{fieldState.error?.message}</p>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="flex flex-col items-center gap-1 text-sm tracking-tight">
              {isExpired ? (
                <p className="text-destructive">{t('verifyRecoveryCode.expired')}</p>
              ) : (
                <>
                  <p className="text-muted-foreground">{t('verifyRecoveryCode.expiresIn')}</p>
                  <p className="text-primary font-medium">{formatCountdown(secondsLeft)}</p>
                </>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <Button
                type="submit"
                className="w-full"
                disabled={isPending || isExpired}
                aria-busy={isPending}
                data-testid="auth-verify-submit"
              >
                {isPending ? t('verifyRecoveryCode.submitting') : t('verifyRecoveryCode.submit')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => navigate('/login')}
                data-testid="auth-verify-back"
              >
                {t('forgotPassword.backToLogin')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </FeatureErrorBoundary>
  );
}
